import { ENROLLMENT_STATUS_LABELS, type EnrollmentCheckDTO, type LeadAutomationDTO, type SequenceSummaryDTO } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pause, Play, Square } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { RelativeTime } from '../../components/crm';
import { Badge, Button, Card, CardBody, CardHeader, Select, useConfirm, useToast } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import { DecisionList } from './DecisionList';

/** Lead profile: which sequence this pizzeria is in, what happens next, and why automation did what it did. */
export function LeadAutomationCard({ businessId }: { businessId: string }) {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [sequenceId, setSequenceId] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const key = ['leads', businessId, 'automation'];

  const { data } = useQuery({ queryKey: key, queryFn: ({ signal }) => api.get<LeadAutomationDTO>(`/api/leads/${businessId}/automation`, { signal }) });
  const { data: sequences } = useQuery({
    queryKey: ['automation', 'sequences'],
    queryFn: ({ signal }) => api.get<{ items: SequenceSummaryDTO[] }>('/api/sequences', { signal }),
    enabled: Boolean(data?.canEdit && !data.liveEnrollment),
  });

  const applied = async (result: LeadAutomationDTO, message: string) => {
    queryClient.setQueryData(key, result);
    toast.success(message);
    await queryClient.invalidateQueries({ queryKey: ['leads', businessId] });
  };
  const act = useMutation({
    mutationFn: ({ action, reason }: { action: 'pause' | 'resume' | 'stop'; reason?: string }) =>
      api.post<LeadAutomationDTO>(`/api/enrollments/${data!.liveEnrollment!.id}/${action}`, { reason }),
    onSuccess: (result, { action }) => applied(result, action === 'pause' ? 'Sequence paused' : action === 'resume' ? 'Sequence resumed' : 'Sequence stopped'),
    onError: (error) => toast.error(errorMessage(error)),
  });
  const enroll = useMutation({
    mutationFn: async () => {
      const check = await api.get<EnrollmentCheckDTO>(`/api/leads/${businessId}/automation/check?sequenceId=${sequenceId}`);
      if (!check.eligible) throw new Error(check.reason);
      const name = sequences?.items.find((s) => s.id === sequenceId)?.name ?? 'this sequence';
      const ok = await confirm({
        title: `Enroll in “${name}”?`,
        message: [`Emails go to ${check.contact?.name ?? check.contact?.email} (${check.contact?.email}).`, ...check.warnings].join('\n'),
        confirmLabel: 'Enroll',
      });
      if (!ok) return null;
      return api.post<LeadAutomationDTO>(`/api/leads/${businessId}/automation/enroll`, { sequenceId, contactId: check.contact?.id });
    },
    onSuccess: async (result) => {
      if (!result) return;
      setSequenceId('');
      await applied(result, 'Enrolled');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (!data) return null;
  const live = data.liveEnrollment;
  const activeSequences = (sequences?.items ?? []).filter((s) => s.isActive && s.stepCount > 0);

  return (
    <Card>
      <CardHeader title="Automation" />
      <CardBody className="space-y-3 text-[13px]">
        {live ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Link to={`/automation/sequences/${live.sequenceId}`} className="font-medium text-slate-900 hover:text-ember-700">
                {live.sequenceName}
              </Link>
              <Badge color={live.status === 'active' ? 'green' : 'amber'} size="sm">
                {ENROLLMENT_STATUS_LABELS[live.status]}
              </Badge>
            </div>
            <p className="text-slate-600">
              Step {Math.min(live.currentStep, live.totalSteps)} of {live.totalSteps} to {live.contactName ?? live.contactEmail}
              {live.nextStep && ` — next: “${live.nextStep.templateName}”`}
              {live.status === 'active' && live.nextRunAt && (
                <>
                  {' '}
                  <RelativeTime value={live.nextRunAt} />
                </>
              )}
            </p>
            {live.statusReason && <p className="text-xs text-slate-500">{live.statusReason}</p>}
            {!live.sequenceActive && <p className="text-xs text-amber-700">The sequence is inactive, so nothing is sent until it is reactivated.</p>}
            {data.canEdit && (
              <div className="flex gap-2">
                {live.status === 'active' ? (
                  <Button size="sm" icon={Pause} loading={act.isPending} onClick={() => act.mutate({ action: 'pause' })}>
                    Pause
                  </Button>
                ) : (
                  <Button size="sm" icon={Play} loading={act.isPending} onClick={() => act.mutate({ action: 'resume' })}>
                    Resume
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  icon={Square}
                  onClick={() =>
                    void (async () => {
                      if (await confirm({ title: 'Stop this sequence?', message: 'Unsent emails from it are cancelled.', confirmLabel: 'Stop', danger: true })) act.mutate({ action: 'stop' });
                    })()
                  }
                >
                  Stop
                </Button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-slate-500">Not in an automated sequence.</p>
        )}

        {!live && data.canEdit && activeSequences.length > 0 && (
          <div className="flex gap-2">
            <Select size="sm" value={sequenceId} onChange={(event) => setSequenceId(event.target.value)} placeholder="Enroll in a sequence…" aria-label="Sequence">
              {activeSequences.map((sequence) => (
                <option key={sequence.id} value={sequence.id}>
                  {sequence.name}
                </option>
              ))}
            </Select>
            <Button size="sm" disabled={!sequenceId} loading={enroll.isPending} onClick={() => enroll.mutate()}>
              Enroll
            </Button>
          </div>
        )}

        {data.decisions.length > 0 && (
          <div>
            <button type="button" className="text-xs text-ember-700 hover:underline" onClick={() => setShowHistory(!showHistory)}>
              {showHistory ? 'Hide' : 'Show'} automation history ({data.decisions.length})
            </button>
            {showHistory && (
              <div className="-mx-4 mt-1">
                <DecisionList items={data.decisions} showLead={false} />
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
